import { useEffect, useRef, useState } from 'react';
import type { AccountRow } from '@/api/client';
import { cx, input } from '@/ui';

function accountName(account: AccountRow): string {
  return account.fullName?.trim() || account.email || `Location #${account.userId}`;
}

export function TenantFilter({
  accounts,
  selectedIds,
  onChange,
}: {
  accounts: AccountRow[];
  selectedIds: number[];
  onChange: (userIds: number[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const label = selectedIds.length === 0 ? 'All locations' : `${selectedIds.length} of ${accounts.length} locations`;

  useEffect(() => {
    if (!open) return;
    function closeOnOutsideClick(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  function toggle(userId: number, checked: boolean) {
    onChange(checked ? [...selectedIds, userId] : selectedIds.filter((id) => id !== userId));
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className={cx(input, 'flex min-w-[132px] items-center justify-between gap-3 disabled:cursor-not-allowed')}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{label}</span>
        <span className="text-neutral-400">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 z-20 mt-1 w-72 rounded-lg border border-neutral-200 bg-white p-2 shadow-lg">
          <div className="flex items-center justify-between border-b border-neutral-100 px-1 pb-2">
            <button
              type="button"
              className="text-xs font-medium text-neutral-950 hover:text-blue-700"
              onClick={() => onChange(accounts.map((account) => account.userId))}
            >
              Select all
            </button>
            <button
              type="button"
              className="text-xs font-medium text-neutral-500 hover:text-neutral-700"
              onClick={() => onChange([])}
            >
              Clear
            </button>
          </div>
          <div className="max-h-72 overflow-auto pt-1">
            {accounts.map((account) => (
              <label
                key={account.userId}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(account.userId)}
                  onChange={(event) => toggle(account.userId, event.target.checked)}
                  className="h-3.5 w-3.5 rounded border-neutral-300 text-neutral-950 focus:ring-blue-500"
                />
                <span className="min-w-0 truncate">
                  {accountName(account)} <span className="text-neutral-400">#{account.userId}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
